import { useState, useEffect, useCallback } from 'react';
import { useSaaSContext } from '../store/SaaSContext';
import {
  SUPPORTED_CURRENCIES,
  CurrencyInfo,
  normalizeCurrencyCode,
  subscribeToRates,
  fetchLiveExchangeRates,
  convertCurrency,
  formatLiveCurrency,
  getExchangeRatePairString,
  LiveRateState,
  getCurrentRatesState
} from '../utils/currencyService';
import { useToast } from '../store/ToastContext';

export function useRealtimeCurrency() {
  const { layout, setLayout } = useSaaSContext();
  const { toast } = useToast();
  const [rateState, setRateState] = useState<LiveRateState>(getCurrentRatesState);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Subscribe to live rate changes
  useEffect(() => {
    const unsubscribe = subscribeToRates((newState) => {
      setRateState(newState);
    });
    return unsubscribe;
  }, []);

  const activeCode = normalizeCurrencyCode(layout.currencySymbol || 'INR');
  const activeCurrencyInfo: CurrencyInfo = SUPPORTED_CURRENCIES[activeCode] || SUPPORTED_CURRENCIES.INR;

  const refreshRates = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const updated = await fetchLiveExchangeRates(true);
      setRateState(updated);
      toast.success(
        'Currency Rates Synced',
        `Live rates updated (Base: USD). 1 USD = ₹${(updated.rates.INR || 95.43).toFixed(2)}`
      );
    } catch {
      toast.error('Sync Error', 'Could not refresh live rates. Using cached rates.');
    } finally {
      setIsRefreshing(false);
    }
  }, [toast]);

  /**
   * Switches active currency and automatically recalculates/converts existing sheet costs,
   * ink costs, and labor rates in real-time.
   */
  const switchCurrency = useCallback((
    newSymbolOrCode: string,
    autoConvertNumericalCosts = true
  ) => {
    const targetCode = normalizeCurrencyCode(newSymbolOrCode);
    const targetInfo = SUPPORTED_CURRENCIES[targetCode] || SUPPORTED_CURRENCIES.INR;
    const oldCode = activeCode;

    if (targetCode === oldCode) return;

    let updatedCostPerSheet = layout.costPerSheet;
    let updatedInkCost = layout.inkCostPerSheet;
    let updatedLaborRate = layout.laborRatePerHour;

    if (autoConvertNumericalCosts) {
      if (layout.costPerSheet !== undefined && layout.costPerSheet > 0) {
        updatedCostPerSheet = convertCurrency(layout.costPerSheet, oldCode, targetCode, rateState.rates);
      }
      if (layout.inkCostPerSheet !== undefined && layout.inkCostPerSheet > 0) {
        updatedInkCost = convertCurrency(layout.inkCostPerSheet, oldCode, targetCode, rateState.rates);
      }
      if (layout.laborRatePerHour !== undefined && layout.laborRatePerHour > 0) {
        updatedLaborRate = convertCurrency(layout.laborRatePerHour, oldCode, targetCode, rateState.rates);
      }
    }

    setLayout({
      ...layout,
      currencySymbol: targetInfo.symbol,
      costPerSheet: updatedCostPerSheet,
      inkCostPerSheet: updatedInkCost,
      laborRatePerHour: updatedLaborRate
    });

    const rateString = getExchangeRatePairString(oldCode, targetCode);
    toast.info(
      `Switched to ${targetInfo.name} (${targetInfo.symbol})`,
      `Values automatically converted via live rate: ${rateString}`
    );
  }, [activeCode, layout, rateState.rates, setLayout, toast]);

  const convert = useCallback((
    amount: number,
    fromCodeOrSymbol?: string,
    toCodeOrSymbol?: string
  ) => {
    const from = fromCodeOrSymbol ? normalizeCurrencyCode(fromCodeOrSymbol) : activeCode;
    const to = toCodeOrSymbol ? normalizeCurrencyCode(toCodeOrSymbol) : activeCode;
    return convertCurrency(amount, from, to, rateState.rates);
  }, [activeCode, rateState.rates]);

  const format = useCallback((
    amount: number,
    codeOrSymbol?: string,
    includeCode = false
  ) => {
    return formatLiveCurrency(amount, codeOrSymbol || activeCurrencyInfo.symbol, includeCode);
  }, [activeCurrencyInfo.symbol]);

  return {
    activeCode,
    activeCurrency: activeCurrencyInfo,
    activeSymbol: activeCurrencyInfo.symbol,
    rates: rateState.rates,
    isLive: rateState.isLive,
    lastUpdated: rateState.lastUpdated,
    isRefreshing,
    refreshRates,
    switchCurrency,
    convert,
    format,
    supportedCurrencies: Object.values(SUPPORTED_CURRENCIES),
    exchangeRateString: (otherCode: string) => getExchangeRatePairString(activeCode, otherCode)
  };
}
